import { createTheme } from '@mui/material/styles';

// ---------------------------------------------------------------------------
// Switchable themes.
//
// The app ships two visual identities that share the same component tree:
//   - "midnight" : the original teal/slate dark theme (default).
//   - "sesh"     : the earthy green/ember theme adapted from mockup/index.html.
//
// Components style themselves off the MUI theme so a switch repaints the whole
// app. Beyond the standard palette, each theme carries a `palette.custom.*`
// token bag for values that don't map onto MUI's palette (panel backgrounds,
// hairline borders, dimmed ink, accent glows, the app body backdrop, fonts).
// makeStyles blocks read these via `theme.palette.custom.*`, so the same style
// rule yields different colors per theme with no per-component branching.
// ---------------------------------------------------------------------------

const sharedTypographyScale = {
  h6: { fontWeight: 600 },
  body1: { fontSize: '0.875rem' },
  body2: { fontSize: '0.75rem' },
};

// --- MIDNIGHT (original) ----------------------------------------------------
const midnight = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#00d9ff' }, // Teal accent
    secondary: { main: '#00ff88' }, // Green accent
    error: { main: '#ff4757' }, // Red for alerts/unread
    background: {
      default: '#0f1115', // Dark slate background
      paper: '#141821', // Slightly lighter for cards/panels
    },
    text: {
      primary: '#ffffff',
      secondary: '#b8bcc8',
    },
    divider: 'rgba(255, 255, 255, 0.1)',
    // Theme-aware token bag (see header). Midnight keeps the values the
    // components used to hardcode so its look is byte-for-byte unchanged.
    custom: {
      // Surfaces
      appBg: '#0f1115', // app-root / video area backdrop
      bodyBackdrop: '#0f1115', // body gradient backdrop (flat here)
      panel: '#1a1f2e', // sunken panels (online counter, tabs, headers)
      panelRaised: '#141821', // raised cards (top bar)
      // Right-rail surface. Midnight keeps an opaque panel (no body glow to
      // reveal), so this matches background.paper for an unchanged look.
      railBg: '#141821',
      // Chat column surface (panel + composer region). Opaque in midnight.
      chatBg: '#141821',
      // The composer INPUT PILL — a solid raised surface on top of chatBg.
      composerBoxBg: '#1a1f2e',
      // Video area: the grid (webcamsContainer) and the docked tiles share this
      // one surface color. Pure black on midnight for a cinema-like backdrop.
      videoAreaBg: '#000000',
      videoTileBg: '#000000',
      // Lines / hairlines
      line: 'rgba(255, 255, 255, 0.1)',
      lineStrong: 'rgba(255, 255, 255, 0.2)',
      // Ink
      ink: '#ffffff',
      inkDim: '#b8bcc8',
      inkFaint: 'rgba(255, 255, 255, 0.45)',
      // Accents
      leaf: '#50ce84', // "on"/online/watching green
      leafDeep: '#3aa869',
      ember: '#ff9900', // warm accent (blocked eye, moderator)
      emberDeep: '#d9551b',
      onColor: '#50ce84',
      offColor: '#64748b',
      // Presence dots — activity-based state (active/idle/away).
      presenceActive: '#50ce84', // green: active (< 5 min)
      presenceIdle: '#f5c518',   // yellow: idle (5–30 min)
      presenceAway: '#ff4d4f',   // red: away/busy (>= 30 min)
      // Legacy webcam-state aliases (kept for any older references).
      presenceLive: '#ff9900',
      presenceOn: '#50ce84',
      // Avatars
      avatarRadius: '50%',
      avatarBg: 'transparent', // no gradient on midnight; avatars are just the image
      // Tint for the default avatar (builtin template 01, see AvatarFallback)
      // shown for users who never picked/uploaded one. Blue on midnight.
      avatarDefaultTint: '#0099ff',
      // Misc
      accentContrastText: '#000000', // text on top of leaf/primary chips
      accentWash: 'rgba(0, 217, 255, 0.1)', // translucent primary tint (banners)
      hoverOverlay: 'rgba(255, 255, 255, 0.05)',
      fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif, "Noto Color Emoji"',
      fontMono: '"Roboto Mono", "Consolas", monospace',
    },
  },
  typography: {
    fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif, "Noto Color Emoji"',
    ...sharedTypographyScale,
  },
  shape: { borderRadius: 8 },
  spacing: 8,
  components: {
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', borderRadius: 8 },
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)' },
        },
      },
    },
    MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          backgroundColor: '#1a1f2e',
          borderRadius: 8,
          marginBottom: 4,
          '&:hover': { backgroundColor: '#212636' },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
          '&:before': { display: 'none' },
        },
      },
    },
  },
});

// --- SESH (mockup-derived) --------------------------------------------------
// Palette lifted from mockup/index.html :root vars. Green + ember on a deep
// earthy near-black, with rounded-square avatars and a soft gradient backdrop.
const sesh = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#6fcf8e' }, // leaf — primary accent now reads green
    secondary: { main: '#ff7a3d' }, // ember
    error: { main: '#ff5a5a' },
    background: {
      default: '#0a0f0c', // bg-0
      paper: '#141d18', // bg-2 — component background
    },
    text: {
      primary: '#e8f0ea', // ink
      secondary: '#93a89a', // ink-dim
    },
    divider: 'rgba(120, 200, 150, 0.10)', // green-tinted hairline
    custom: {
      // Surfaces
      appBg: '#0a0f0c',
      // The mockup body backdrop: layered radial glows over bg-0.
      bodyBackdrop:
        'radial-gradient(1200px 700px at 18% -10%, rgba(111, 207, 142, 0.10), transparent 60%),' +
        'radial-gradient(900px 600px at 95% 0%, rgba(255, 122, 61, 0.07), transparent 55%),' +
        'radial-gradient(1000px 800px at 50% 120%, rgba(58, 154, 92, 0.08), transparent 60%),' +
        '#0a0f0c',
      panel: '#1b2620', // bg-3 — sunken panels
      panelRaised: '#141d18', // bg-2 — top bar / raised cards
      // Right-rail surface — mockup `.rail`: a translucent green-tinted panel so
      // the body's radial-glow backdrop bleeds through (vs. a flat opaque slab).
      railBg: 'rgba(15, 22, 18, 0.45)',
      // Chat column surface — mockup `.chat`: a faint translucent wash so the
      // glow shows through behind the messages + composer (vs. a flat slab).
      chatBg: 'rgba(10, 15, 12, 0.35)',
      // The composer INPUT PILL — mockup `.composer-box`: a SOLID raised surface
      // (bg-2) sitting on the translucent chat region.
      composerBoxBg: '#141d18',
      // Video area: grid + docked tiles share this. Matches the sesh app-bg
      // (bg-0) so the tile blends into the grid (sesh look unchanged).
      videoAreaBg: '#0a0f0c',
      videoTileBg: '#141d18',
      // Lines / hairlines (green-tinted)
      line: 'rgba(120, 200, 150, 0.10)',
      lineStrong: 'rgba(120, 200, 150, 0.20)',
      // Ink
      ink: '#e8f0ea',
      inkDim: '#93a89a',
      inkFaint: '#5f7468',
      // Accents
      leaf: '#6fcf8e',
      leafDeep: '#3a9a5c',
      ember: '#ff7a3d',
      emberDeep: '#d9551b',
      onColor: '#6fcf8e',
      offColor: '#5f7468',
      // Presence dots — activity-based state (active/idle/away). Tuned to the
      // sesh palette (slightly warmer green, amber idle, soft red away).
      presenceActive: '#6fcf8e', // green: active (< 5 min)
      presenceIdle: '#e8b339',   // amber: idle (5–30 min)
      presenceAway: '#e35d5d',   // red: away/busy (>= 30 min)
      // Legacy webcam-state aliases (kept for any older references).
      presenceLive: '#ff7a3d',
      presenceOn: '#6fcf8e',
      // Avatars — mockup uses rounded squares, not circles
      avatarRadius: '11px',
      avatarBg: 'linear-gradient(135deg, #6fcf8e, #3a9a5c)',
      // Tint for the default avatar (builtin template 01, see AvatarFallback)
      // shown for users who never picked/uploaded one. Leaf green on sesh.
      avatarDefaultTint: '#6fcf8e',
      // Misc
      accentContrastText: '#06140c', // dark ink on leaf chips
      accentWash: 'rgba(111, 207, 142, 0.12)', // translucent leaf tint (banners)
      hoverOverlay: 'rgba(255, 255, 255, 0.035)',
      fontFamily: '"Bricolage Grotesque", "Outfit", sans-serif, "Noto Color Emoji"',
      fontMono: '"Space Mono", "Roboto Mono", monospace',
    },
  },
  typography: {
    fontFamily: '"Bricolage Grotesque", "Outfit", sans-serif, "Noto Color Emoji"',
    ...sharedTypographyScale,
  },
  shape: { borderRadius: 10 },
  spacing: 8,
  components: {
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', borderRadius: 10 },
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: '0 6px 18px rgba(0, 0, 0, 0.4)' },
        },
      },
    },
    MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          backgroundColor: '#1b2620',
          borderRadius: 10,
          marginBottom: 4,
          '&:hover': { backgroundColor: '#22332a' },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
          '&:before': { display: 'none' },
        },
      },
    },
  },
});

export const themes = { midnight, sesh };

// Human-facing labels for the theme switcher.
export const themeMeta = {
  midnight: { label: 'Midnight', accent: '#00d9ff' },
  sesh: { label: 'Sesh', accent: '#6fcf8e' },
};

export const DEFAULT_THEME = 'midnight';

export const getTheme = (name) => themes[name] || themes[DEFAULT_THEME];

// Default export stays the original midnight theme so any existing
// `import theme from "./theme"` keeps working unchanged.
export default midnight;
